/**
 * An image's own dimensions, and a placeholder small enough to travel.
 *
 * WHY, in the user's words (Malaika, 2026-09-11): "everyone should see the
 * message and the image show up at the exact same time … so as not to cause
 * confusion even when on opposite devices."
 *
 * That is impossible with what the shared message record carries today —
 * `name`, `type`, `size`, a locator. A recipient has nothing to paint until
 * the full bytes arrive and decode, and nothing to reserve space with either,
 * so the text lands, sits alone, and the bubble jumps when the picture
 * finally appears. Text-first-image-later is structural, not a slow network.
 *
 * Two artifacts fix it, and NEITHER TOUCHES THE UPLOADED FILE. The owner's
 * ruling of 2026-09-11 is that images are never downscaled — fidelity is
 * preserved for case evidence — and it is honoured literally: what goes up is
 * byte-identical to what was picked (pinned in attachmentUploadTiming). These
 * are a MEASUREMENT and a SEPARATE tiny image, produced beside the original.
 *
 *   width / height  — so the tile reserves the true box and nothing reflows.
 *   preview         — a ~24px JPEG, sub-kilobyte, painted instantly and
 *                     blurred up while the real bytes travel.
 *
 * THE SIZE CAP IS LOAD-BEARING, not tidiness. The first attachment defect
 * this repo ever recorded (attachmentDurability, 2026-08-20) was a single
 * oversized inline `dataUrl` that pushed a thread document past Firestore's
 * 1 MiB limit — after which EVERY later write to that thread failed silently
 * and the transcript froze server-side at the message before the attachment.
 * A preview is base64 on a shared document, i.e. the same shape. Hence a hard
 * ceiling here, a second one in the server sanitizer, and a test on both.
 *
 * No Firebase imports, deliberately: the tile renders this, and reaching for
 * a module that imports `firebase/auth` takes three chat-isolation suites
 * down at import time (the 2026-09-11 lesson).
 */

/** The longest edge of the placeholder, in pixels. */
export const PREVIEW_MAX_EDGE = 24;

/**
 * The most a preview may weigh, in base64 characters.
 *
 * 1.5 KB × 10 attachments is ~15 KB on a document budgeted at 1 MiB — room
 * enough that a preview can never be the reason a write fails. A JPEG of a
 * 24px edge lands far under this; the cap exists for the case that does not.
 */
export const PREVIEW_MAX_CHARS = 1_500;

export interface AttachmentImageMeta {
  width?: number;
  height?: number;
  /** A `data:image/jpeg;base64,…` URI at most PREVIEW_MAX_CHARS long. */
  preview?: string;
}

/**
 * Everything one decode of a picked image can yield: the record fields, and —
 * only for a format a recipient's browser may not paint — a JPEG to upload
 * beside the original so they see the photograph rather than a file card.
 *
 * The rendition is a Blob, NOT a record field: it has to be uploaded, and
 * spreading it into the attachment record would put a Blob on a Firestore
 * document. The caller uploads it and records where it went.
 */
export interface AttachmentImageArtifacts {
  meta: AttachmentImageMeta;
  displayRendition?: Blob;
}

/** Images we can decode AND re-encode for a placeholder. */
const PREVIEWABLE = /^image\/(png|jpe?g|gif|webp|avif|bmp)$/i;

/**
 * Formats this browser may be able to decode that ANOTHER browser cannot paint
 * (2026-09-12). HEIC is the whole list in practice: it is the iPhone camera's
 * default, Safari decodes it natively because it is the platform's own format,
 * and Chrome and Windows cannot — so a photo taken on a phone reached a
 * teammate as a file card they could not open.
 *
 * The answer is a DISPLAY RENDITION: a bounded JPEG produced beside the
 * original, from the decode that already happens here for the dimensions and
 * the placeholder. The original still uploads BYTE-IDENTICAL and remains the
 * record and the download — the owner's no-downscale ruling is about the file
 * the user sent, and this is an additional artifact exactly as `preview` is.
 *
 * The extension is honoured alongside the mime because iOS hands over a .heic
 * with an empty `type` often enough to matter.
 */
const NEEDS_DISPLAY_RENDITION_MIME = /^image\/(heic|heif)(-sequence)?$/i;
const NEEDS_DISPLAY_RENDITION_EXT = /\.(heic|heif)$/i;

/** The rendition's longest edge. Big enough to look like the photo, small
 *  enough that it is plainly a display copy and not a second original. */
export const RENDITION_MAX_EDGE = 2048;

/** JPEG quality for the rendition — visually clean, roughly a fifth the bytes. */
export const RENDITION_QUALITY = 0.85;

export function needsDisplayRendition(name: string | undefined, type: string | undefined): boolean {
  return NEEDS_DISPLAY_RENDITION_MIME.test(String(type || ''))
    || NEEDS_DISPLAY_RENDITION_EXT.test(String(name || ''));
}

/**
 * Measure an image and build its placeholder. Never throws, never blocks a
 * send: every failure — an unsupported codec, a browser without
 * `createImageBitmap`, a canvas the platform taints or refuses — returns an
 * empty object, and an empty object means the record is exactly what it is
 * today. This is an enhancement that must be free to be absent.
 */
/**
 * Pictures larger than this are not decoded for their placeholder (2026-10-08,
 * the 50 GB raise). createImageBitmap decodes the WHOLE image into memory;
 * the picker accepts any image up to the upload limit, and a 2 GB TIFF decoded
 * beside its own upload can take the tab with it. Such a file is sent exactly
 * as it is, with no placeholder — the honest file card it had before this
 * module existed.
 */
export const IMAGE_META_MAX_BYTES = 100 * 1024 * 1024;

export async function readAttachmentImageMeta(file: File): Promise<AttachmentImageArtifacts> {
  if (!file) return { meta: {} };
  if (Number(file.size) > IMAGE_META_MAX_BYTES) return { meta: {} };
  const decodable = PREVIEWABLE.test(String(file.type || ''))
    || needsDisplayRendition(file.name, file.type);
  if (!decodable) return { meta: {} };
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return { meta: {} };

  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    const width = bitmap.width;
    const height = bitmap.height;
    if (!(width > 0) || !(height > 0)) return { meta: {} };

    const scale = PREVIEW_MAX_EDGE / Math.max(width, height);
    // Already tiny: the original IS its own placeholder-sized image, and
    // scaling up would only invent pixels.
    const w = Math.max(1, Math.round(width * Math.min(1, scale)));
    const h = Math.max(1, Math.round(height * Math.min(1, scale)));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { meta: { width, height } };
    ctx.drawImage(bitmap, 0, 0, w, h);

    let preview: string | undefined;
    try {
      const url = canvas.toDataURL('image/jpeg', 0.5);
      // A canvas the browser considers tainted throws above; a data URI that
      // came back too heavy is dropped rather than trimmed, because half a
      // base64 string is not an image.
      if (typeof url === 'string' && url.startsWith('data:image/') && url.length <= PREVIEW_MAX_CHARS) {
        preview = url;
      }
    } catch { /* no preview; the dimensions alone still stop the reflow */ }

    const meta: AttachmentImageMeta = preview ? { width, height, preview } : { width, height };

    // The display rendition, for a format the recipient may not decode. Same
    // bitmap, so a HEIC is decoded ONCE on the sender's phone for the
    // dimensions, the placeholder and this.
    let displayRendition: Blob | undefined;
    if (needsDisplayRendition(file.name, file.type)) {
      displayRendition = await renderDisplayJpeg(bitmap, width, height);
    }

    return displayRendition ? { meta, displayRendition } : { meta };
  } catch {
    return { meta: {} };
  } finally {
    try { bitmap?.close(); } catch { /* not every implementation has close() */ }
  }
}

/**
 * A bounded JPEG of an already-decoded bitmap. Null on anything that fails —
 * no canvas, a tainted canvas, a platform whose toBlob refuses JPEG — because
 * the absence of a rendition is the honest file card, which is what a
 * recipient got before this existed.
 */
async function renderDisplayJpeg(
  bitmap: ImageBitmap,
  width: number,
  height: number,
): Promise<Blob | undefined> {
  try {
    const scale = Math.min(1, RENDITION_MAX_EDGE / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', RENDITION_QUALITY);
      } catch {
        resolve(null);
      }
    });
    // A zero-byte blob is not a picture; treat it as no rendition at all.
    return blob && blob.size > 0 ? blob : undefined;
  } catch {
    return undefined;
  }
}
